import { takeLatest, call, put, all, take, select } from "redux-saga/effects";

import {
  updateShortlistedSuccess,
  removeShortlistedSuccess,
  sendInviteSuccess,
  getRoleSearchObjectSuccess,
  getRoleSearchObjectFailure,
  searchPeopleSuccess,
  searchPeopleFailure,
  searchPeople,
  getFilteredTiersListSuccess,
  getFilteredTiersListError,
  updateShortlistedStatusError,
  updateShortlistedStatusSuccess
} from "../actions/tiers";
import actionTypes from "../types";
import {
  fetchTiers,
  fetchTiersJobId,
  fetchRoleInviteData,
  fetchInviteTemplate,
  getSearchObjectByRoleUUID,
  searchPeopleByPayload,
  shortlistPerson,
  deleteShortlistedPerson,
  triggerInviteToTalent,
  updateShortlistedPersonStatus,
  updateTalentAcceptedStatus,
  updateTalentRejectedStatus,
  updateTalentUnsubscribedStatus,
  updateTalentWaitlistedStatus,
  deleteOutstandingInvite
} from "api/tiers";
import { setSessionValue } from "components/pages/talents/talentListNew/helper";
import { logoutUser } from "./auth";
import { getInviteTimeLabel, isScheduled } from "../../globalFunctions/tiers";

const {
  UPDATE_SHORTLISTED,
  REMOVE_SHORTLISTED,
  SEND_INVITE,
  GENERATE_TIERS,
  TIERS_JOB,
  GET_ROLE_DATA,
  GET_ROLE_SEARCH_OBJECT,
  SEARCH_PEOPLE,
  UPDATE_SHORTLISTED_STATUS,
  CONFIRM_INVITE,
  REJECT_INVITE,
  UNSUBSCRIBE,
  WAITLIST_INVITE
} = actionTypes.tiers;

export function* verifyApiStatus(response) {
  if (response?.status === 401) {
    yield call(logoutUser)
  }
}

export function* updateShortlistedTalents(action) {
  const { data, selectedRoleUUID, onSuccess, onError } = action.payload;

  try {
    const payload = {
      event_shift2role_uuid: selectedRoleUUID,
      person_uuid: data.uuid,
      tier: 0
    };

    const response = yield call(shortlistPerson, payload);
    yield call(verifyApiStatus, response);

    if (response?.data) {
      onSuccess(response.data);
    }
  } catch (error) {
    onError(error.message);
    yield call(verifyApiStatus, error?.response);
  }
}

export function* removeShortlistedItem(action) {
  const { payload } = action;
  const { uuid } = payload;
  try {
    const shortlisted = yield select((state) => state.tiers.shortlisted);
    const shortlistedIndex = shortlisted.findIndex(
      (item) => item.event_shift2role2person_uuid === uuid
    );
    const shortlistedItem = shortlisted[shortlistedIndex];
    if (shortlistedItem?.outStandingJob && shortlistedItem?.outStandingJob.uuid) {
      yield call(deleteOutstandingInvite, shortlistedItem.outStandingJob.uuid);
    }
    const response = yield call(deleteShortlistedPerson, uuid)
    yield call(verifyApiStatus, response);
    if (response?.data?.code === 'EVENT_SHIFT2ROLE2PERSON-DELETED') {
      yield put(removeShortlistedSuccess({ uuid }));
    }
  } catch (error) {
    console.error('Error on removing shortlisted item', error)
    yield call(verifyApiStatus, error?.response);
  }
}

export function* sendInviteToTalent(action) {
  const { payload } = action;
  const { inviteData, onSuccess, onError } = payload;
  const { event_shift2role2person_uuid } = inviteData;

  try {
    const shortlisted = yield select((state) => state.tiers.shortlisted);
    const loggedInUserId = yield select((state) => state.auth.loginDetails.person_uuid);

    const existingItem = shortlisted.find(
      (item) => item.roleUUID === event_shift2role2person_uuid
    );

    if (existingItem?.outStandingJob?.uuid) {
      yield call(deleteOutstandingInvite, existingItem.outStandingJob.uuid);
    }

    const response = yield call(triggerInviteToTalent, inviteData);
    yield call(verifyApiStatus, response);
    const updatedData = response?.data?.[0];

    if (!updatedData) {
      onSuccess();
      return;
    }
    const { outstanding_job } = updatedData;

    const fireAfter = outstanding_job?.fire_after;

    const inviteQuotaFilled = existingItem?.talentNeeded <= (updatedData?.status === "approved" ? existingItem?.approvedStats + 1 : existingItem?.approvedStats)

    const updatedItem = {
      ...existingItem,
      inviteStatus: updatedData?.status,
      outStandingJob: outstanding_job,
      inviteQuotaFilled: inviteQuotaFilled,
      inviteTimeLabel: getInviteTimeLabel({
        outStandingJob: outstanding_job,
        timezone: existingItem?.timezone,
        tzAbbr: existingItem?.tzAbbr,
        loggedInUserId
      }),
      isScheduled: isScheduled(fireAfter, existingItem?.timezone)
    };

    yield put(sendInviteSuccess({ updatedItem }));

    onSuccess();

  } catch (error) {
    onError(error.message);
    yield call(verifyApiStatus, error?.response);
  }
}

export function* fetchRoleData(action) {
  const { payload } = action;
  const { onSuccess, onError, id, slug } = payload;
  try {
    const response = yield call(fetchRoleInviteData, id);
    yield call(verifyApiStatus, response);
    const data = response?.data;
    if (data) {
      const inviteTemplateResponse = yield call(fetchInviteTemplate, { data, slug });
      yield call(verifyApiStatus, inviteTemplateResponse);
      const inviteTemplateData = inviteTemplateResponse?.data
      if (inviteTemplateData) {
        onSuccess(inviteTemplateData, data?.token);
      }
    }
  } catch (error) {
    onError(error.message);
    yield call(verifyApiStatus, error?.response);
  }
}

export function* getTiers(action) {
  const { payload } = action;
  const { onSuccess, onError, id } = payload;
  try {
    yield call(fetchTiersJobId, id);
    onSuccess();
  } catch (error) {
    onError(error.message);
    yield call(verifyApiStatus, error?.response);
  }
}

export function* fetchTiersData(action) {
  const { payload } = action;
  const { onSuccess, onError, id } = payload;
  try {
    yield call(fetchTiers, id);
    onSuccess();
  } catch (error) {
    onError(error.message);
    yield call(verifyApiStatus, error?.response);
  }
}

export function* getRoleSearchObjectSaga(action) {
  const { roleUUID, number = 1, size = 50, onSuccess, onError } = action.payload;

  try {
    const response = yield call(getSearchObjectByRoleUUID, roleUUID);
    yield call(verifyApiStatus, response);
    const { data } = response

    const payload = {
      data,
      number,
      size
    }
    const list = yield call(searchPeopleSaga, ({ payload, reset: true }));
    onSuccess(data, list);
  } catch (error) {
    yield put(getRoleSearchObjectFailure(error));
    yield call(verifyApiStatus, error?.response);
  }
}

export function* searchPeopleSaga(action) {
  const { payload, reset = false } = action;

  try {
    const response = yield call(searchPeopleByPayload, payload);
    yield call(verifyApiStatus, response);
    const filteredTalentsList = response?.data;
    const totalItemCount = response?.headers['total-item-count']
    const pageSize = response?.headers['page-size']
    const totalPageCount = response?.headers['total-page-count']
    const responsePagenumber = response?.headers['response-page-number']
    yield put(getFilteredTiersListSuccess({ filteredTalentsList, totalItemCount, pageSize, totalPageCount, responsePagenumber }));
    return filteredTalentsList;
  } catch (error) {
    yield put(getFilteredTiersListError(error));
    yield call(verifyApiStatus, error?.response);
  }
}

export function* updateShortlistedTalentStatus(action) {
  const { payload } = action;
  try {
    const { uuid } = payload
    const shortlisted = yield select((state) => state.tiers.shortlisted);
    const loggedInUserId = yield select((state) => state.auth.loginDetails.person_uuid);

    const existingItem = shortlisted.find(
      (item) => item.roleUUID === uuid
    );

    const response = yield call(updateShortlistedPersonStatus, payload);
    yield call(verifyApiStatus, response);
    const { data } = response
    const { outstanding_jobs } = data;

    const fireAfter = outstanding_jobs[0]?.fire_after;
    const inviteQuotaFilled = existingItem?.talentNeeded <= (data?.status === "approved" ? existingItem?.approvedStats + 1 : existingItem?.approvedStats)

    const updatedItem = {
      ...existingItem,
      inviteStatus: data?.status,
      outStandingJob: outstanding_jobs || [],
      inviteQuotaFilled: inviteQuotaFilled,
      inviteTimeLabel: getInviteTimeLabel({
        outStandingJob: outstanding_jobs[0],
        timezone: existingItem?.timezone,
        tzAbbr: existingItem?.tzAbbr,
        loggedInUserId
      }),
      isScheduled: isScheduled(fireAfter, existingItem?.timezone)
    };


    yield put(updateShortlistedStatusSuccess({ updatedItem }));
  } catch (error) {
    yield put(updateShortlistedStatusError(error));
    yield call(verifyApiStatus, error?.response);
  }
}

export function* updateTalentAccepted(action) {
  const { payload } = action;
  const { onSuccess, onError, token } = payload;
  try {
    const response = yield call(updateTalentAcceptedStatus, token);
    if (response?.status < 200 || response?.status >= 300) {
      return onError();
    }
    onSuccess();
  } catch (error) {
    console.error('Error while updating talent to accepted status', error)
    onError();
  }
}

export function* updateTalentRejected(action) {
  const { payload } = action;
  const { onSuccess, onError, token } = payload;
  try {
    const response = yield call(updateTalentRejectedStatus, token);
    if (response?.status < 200 || response?.status >= 300) {
      return onError();
    }
    onSuccess();
  } catch (error) {
    console.error('Error while updating talent to accepted status', error)
    onError();
  }
}

export function* updateTalentWaitlisted(action) {
  const { payload } = action;
  const { onSuccess, onError, token } = payload;
  try {
    const response = yield call(updateTalentWaitlistedStatus, token);
    if (response?.status < 200 || response?.status >= 300) {
      return onError();
    }
    onSuccess();
  } catch (error) {
    console.error('Error while updating talent to accepted status', error)
    onError();
  }
}

export function* updateTalentUnsubscribed(action) {
  const { payload } = action;
  const { onSuccess, onError, token } = payload;
  try {
    const response = yield call(updateTalentUnsubscribedStatus, token);
    if (response?.status < 200 || response?.status >= 300) {
      return onError();
    }
    onSuccess();
  } catch (error) {
    console.error('Error while updating talent to accepted status', error)
    onError();
  }
}

export function* saga() {
  yield takeLatest(UPDATE_SHORTLISTED, updateShortlistedTalents);
  yield takeLatest(REMOVE_SHORTLISTED, removeShortlistedItem);
  yield takeLatest(SEND_INVITE, sendInviteToTalent);
  yield takeLatest(GET_ROLE_DATA, fetchRoleData);
  yield takeLatest(GENERATE_TIERS, getTiers);
  yield takeLatest(TIERS_JOB, fetchTiersData);
  yield takeLatest(GET_ROLE_SEARCH_OBJECT, getRoleSearchObjectSaga);
  yield takeLatest(SEARCH_PEOPLE, searchPeopleSaga);
  yield takeLatest(UPDATE_SHORTLISTED_STATUS, updateShortlistedTalentStatus);
  yield takeLatest(CONFIRM_INVITE, updateTalentAccepted);
  yield takeLatest(REJECT_INVITE, updateTalentRejected);
  yield takeLatest(WAITLIST_INVITE, updateTalentWaitlisted);
  yield takeLatest(UNSUBSCRIBE, updateTalentUnsubscribed);
}

export function* tierSagas() {
  yield all([call(saga)]);
}
